import { useMutation, useQueryClient } from "@tanstack/react-query";
import FormGroupTile from "components/core/basic/FormGroupTile";
import BulkTagsForm from "components/core/blocks/BulkTagsForm";
import { getScheduledBidChangeValue, TARGET_STATES } from "components/custom/category/advertisements/MicroStores2";
import moment from "moment";
import { useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useAppSelector } from "redux/hooks";
import api from "utils/api";
import { formatCurrency } from "utils/formatNumber";
import { snakeCaseToSentenceCase, toTitleCase } from "utils/formatText";
import { successToast, errorToast } from "utils/toasts";
import BulkAdPlanActionForm from "views/advertising/BulkAdPlanActionForm";

const useTargetBulkActions = (selectedTargets, setSelectedTargets, targetType, targetData, isNTMAd = false) => {
  const [bidValue, setBidValue] = useState("");
  const [minBid, setMinBid] = useState("");
  const [maxBid, setMaxBid] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [bidBoostFormData, setBidBoostFormData] = useState({
    actionUnit: "percent",
    startDate: moment().format("YYYY-MM-DD"),
    endDate: moment().add(2, "days").format("YYYY-MM-DD"),
    keepBoostedBid: true,
  });
  const [filledTags, setFilledTags] = useState([]);

  const queryClient = useQueryClient();
  const channel = useAppSelector((state) => state.channel);
  const { marketPlace } = useAppSelector((state) => state.marketPlace);

  const importedTargetIds = useMemo(() => {
    return targetData?.keywords?.filter((target) => target.target_source === "imported")?.map((t) => t.id) ?? [];
  }, [targetData]);

  const skippedTargetCount = useMemo(() => {
    return selectedTargets?.filter((targetId) => importedTargetIds?.includes(targetId))?.length ?? 0;
  }, [importedTargetIds, selectedTargets]);

  const customTags = useMemo(() => {
    return [
      ...new Set(
        targetData?.keywords
          ?.map((target) => target.tags ?? [])
          ?.flat()
          .filter((tag) => tag),
      ),
    ];
  }, [targetData]);

  const updateTargetHandler = useMutation({
    mutationFn: async (updateData) => {
      return await api.post("/target_actions/", updateData);
    },
    onSuccess: (data, vars) => {
      if ("isLast" in vars === false || ("isLast" in vars && vars["isLast"])) {
        const targetType = data.target_type;
        resetActionFormFields();
        setSelectedTargets([]);

        successToast({
          message: `${toTitleCase(targetType)} Successfully Updated`,
        });
        queryClient.invalidateQueries();
      }
    },
    onError: (error) => {
      if (error.response.data && "error" in error.response.data) {
        const formattedError = error.response.data.error[0]?.split(":")[1] ?? "Error Updating Targets";
        errorToast({ message: formattedError, error });
        return;
      }

      const targetType = error.target_type;
      errorToast({ message: `Error Updating ${toTitleCase(targetType)}`, error });
    },
  });

  const scheduleHandler = useMutation({
    mutationFn: async (body) => {
      const reqData = body.data ?? {};

      return await api.post("/api/scheduledactions/", reqData);
    },

    onSuccess: (data, variables) => {
      if (!variables.isLast) {
        return;
      }
      successToast({
        message: `Successfully Scheduled Bid Update${selectedTargets?.length > 1 ? "s" : ""}`,
      });

      setSelectedTargets([]);
      setBidBoostFormData({
        actionUnit: "percent",
        startDate: moment().format("YYYY-MM-DD"),
        endDate: moment().add(2, "days").format("YYYY-MM-DD"),
        keepBoostedBid: true,
      });
      queryClient.invalidateQueries();
    },
    onError: (error, vars) => {
      const errorDetails = error.response?.data?.non_field_errors ?? [];
      const adIds = vars.data.gvadvertisements_set;

      if (errorDetails.join("").includes("Date range overlaps with another scheduled action")) {
        errorToast({
          message: `Failed to create scheduled update for media plan${adIds.length === 1 ? "" : "s"} ${adIds.join(",")}. The selected date range collides with a previously scheduled action.`,
          error,
        });
        return;
      }

      if (vars.isLast) {
        errorToast({ message: `Failed to update Media Plan${vars.data.gvadvertisements_set.length === 1 ? "" : "s"}` });
      }
      errorToast({ message: `Failed to Schedule Bid Update${selectedTargets?.length > 1 ? "s" : ""}`, error });
    },
  });

  const negateTargets = () => {
    const eligibleTargets = selectedTargets?.filter((targetId) => !importedTargetIds?.includes(targetId));
    const updateData = {
      target_ids: eligibleTargets,
      values: [],
      action: "negate_target",
      target_type: targetType,
      channel: channel.currentChannel ?? "amazon",
    };

    updateTargetHandler.mutate({ data: updateData });
  };

  const updateTargetState = (newState) => {
    const updateData = {
      target_ids: selectedTargets,
      values: [newState],
      action: "change_state",
      target_type: targetType,
      channel: channel.currentChannel ?? "amazon",
    };

    updateTargetHandler.mutate({
      data: updateData,
    });
  };
  const updateTargetBids = async (bulk = false, bulkActionType = null, changes) => {
    let updateData = {};

    // Case of edited cell values or bulk updated with shared bid value
    if (bulk) {
      updateData = {
        target_ids: selectedTargets,
        values: [],
        action: bulkActionType,
        target_type: targetType,
        channel: channel.currentChannel ?? "amazon",
      };

      switch (bulkActionType) {
        case "set_bid_range":
          updateData.values = [parseFloat(maxBid), parseFloat(minBid)];
          break;
        case "clear_bid_range":
          updateData.action = "set_bid_range";
          updateData.values = [null, null];
          break;
        case "set_bid":
          if (!bidValue) {
            updateData.values = [null];
          } else {
            updateData.values = [parseFloat(bidValue)];
          }
          break;
        case "boost_bid":
          updateData.values = [];

          if (bidBoostFormData?.startDate) {
            updateData.budget_start_date = bidBoostFormData?.startDate;
          }
          if (bidBoostFormData?.endDate) {
            updateData.budget_end_date = bidBoostFormData?.endDate;
          }
          break;
        default:
          break;
      }

      updateTargetHandler.mutate({
        data: updateData,
        // Single request, so mark true to launch success/failure notification
        isLast: true,
      });

      return;
    }

    const editTargetData = targetData?.keywords
      ?.filter((row) =>
        Object.keys(changes)
          ?.map((id) => parseInt(id))
          ?.includes(row.id),
      )
      ?.map((row) => {
        return { ...row, ...changes[row.id] };
      });

    for (let i = 0; i < editTargetData?.length; i++) {
      const updatedTargetData = targetData?.keywords?.find(
        (target) => target.target_id === editTargetData[i].target_id,
      );

      let updateData = {};

      updateData = {
        target_ids: [updatedTargetData?.target_id],
        values: [parseFloat(editTargetData[i]?.target_bid)],
        action: "set_bid",
        target_type: targetType,
        channel: channel.currentChannel ?? "amazon",
      };

      updateTargetHandler.mutate({
        data: updateData,
        isLast: i === editTargetData?.length - 1,
      });
    }
  };

  const scheduleBidBoost = async (actionType) => {
    for (let i = 0; i < selectedTargets?.length; i++) {
      const { actionValue, actionUnit, startDate, endDate, keepBoostedBid } = bidBoostFormData;
      let rollbackBid = true;

      if (keepBoostedBid) {
        rollbackBid = false;
      }

      const updateData = {
        channel: channel.currentChannel === "walmart" ? "walmart" : "AMZ",
        status: "scheduled",
        start_date: `${startDate.split("T")[0]}T00:00:00.000000`,
        end_date: `${endDate.split("T")[0]}T00:00:00.000000`,
        adv_keyword: selectedTargets[i],
        gvadvertisements_set: [],
        gvadvertisedkeyword_set:
          targetType === "keyword" && channel.currentChannel === "amazon" ? [selectedTargets[i]] : [],
        gvadvertisedtarget_set:
          targetType === "target" && channel.currentChannel === "amazon" ? [selectedTargets[i]] : [],
        walmartadgroupkeyword_set:
          targetType === "keyword" && channel.currentChannel === "walmart" ? [selectedTargets[i]] : [],
        walmartaditem_set: targetType === "target" && channel.currentChannel === "walmart" ? [selectedTargets[i]] : [],

        fields: [
          {
            field_name:
              actionType === "fixed" ? "manual_bid"
              : actionUnit === "percent" ? "timebound_per_change"
              : "timebound_flat_change",
            change_type: "set_change",
            // For percent bid boost values, convert into multiplier (i.e. 50% add to original becomes 1.5)
            change_value: getScheduledBidChangeValue(actionValue, actionUnit, actionType),
            rollback: rollbackBid,
          },
        ],
      };

      scheduleHandler.mutate({
        data: updateData,
        isLast: i === selectedTargets?.length - 1,
      });
    }
  };

  const deleteHandler = useMutation({
    mutationFn: async (data) => {
      const { id } = data;
      return await api.remove(`/api/scheduledactions/${id}/`);
    },

    onSuccess: (data, variables) => {
      if (!variables.isLast) {
        return;
      }
      successToast({
        message: "Successfully Cancelled Scheduled Bid Boosts",
      });
      setSelectedTargets([]);
      setBidBoostFormData({
        actionUnit: "percent",
        startDate: moment().format("YYYY-MM-DD"),
        endDate: moment().add(2, "days").format("YYYY-MM-DD"),
        keepBoostedBid: true,
      });
      queryClient.invalidateQueries();
    },

    onError: (error) => {
      errorToast({ message: "Failed to Cancel Scheduled Bid Boosts", error });
    },
  });

  const cancelScheduledBidBoosts = async () => {
    for (let i = 0; i < selectedTargets?.length; i++) {
      const data = targetData?.keywords?.find((target) => target.target_id === selectedTargets[i]);

      // Filter out only scheduled and active ids

      const ids = data.scheduled_actions
        ?.filter((a) => ["scheduled", "active"].includes(a.scheduled_action_status))
        .map((a) => a.scheduled_action_id);

      for (let j = 0; j < ids.length; j++) {
        deleteHandler.mutate({ id: ids[j], isLast: i === selectedTargets.length - 1 });
      }
    }
  };

  const updatePlanTags = (type = "add") => {
    for (let i = 0; i < selectedTargets.length; i++) {
      const target = targetData?.keywords?.find((target) => target.id === selectedTargets[i]);
      let updatedTags =
        type === "remove" ?
          (target?.tags?.filter((tag) => !filledTags?.includes(tag)) ?? [])
        : [...(target?.tags ?? []), ...filledTags];
      updatedTags = [...new Set(updatedTags)];

      const updateData = {
        target_ids: [target.id],
        values: [updatedTags],
        action: "update_tags",
        target_type: targetType,
        channel: channel.currentChannel ?? "amazon",
      };

      updateTargetHandler.mutate({
        data: updateData,
        isLast: i === selectedTargets?.length - 1,
      });
    }
  };

  const resetActionFormFields = () => {
    setBidValue(null);
    setMinBid(null);
    setMaxBid(null);
    setStartDate(null);
    setEndDate(null);
    setFilledTags([]);
  };

  let bulkActionOptions = [
    {
      label: `Set Fixed Bid`,
      action: () => updateTargetBids(true, "set_bid"),
      actionFormFields: (
        <Row className="pb-5">
          <form>
            <p className="pb-4 fs-4">
              Please enter your desired bid value for the selected {targetType}
              {selectedTargets?.length > 1 ? "s" : ""} using the field below.
            </p>
            <Row className="pt-3 d-flex justify-content-center">
              <Col xs={12} md={6} lg={4} className="px-5">
                <FormGroupTile
                  type="formcontrol"
                  formControlType={"number"}
                  handleChildFormElement={(key, value) => {
                    setBidValue(value);
                  }}
                  label={"Bid Amount"}
                  placeholder={"Enter a bid value"}
                  defaultValue={bidValue}
                />
              </Col>
            </Row>
          </form>
        </Row>
      ),
      confirmationMessage: (
        <>
          {startDate > endDate ?
            <p className="py-2 text-danger fs-5">Invalid date range. Start date must come before end date.</p>
          : parseFloat(bidValue) ?
            <p className="fs-4 py-2">
              Are you sure you want to update bids for the selected {targetType}
              {selectedTargets?.length > 1 ? "s" : ""} to {formatCurrency(bidValue, marketPlace)}?
            </p>
          : null}
        </>
      ),
      disableConfirmButton: !parseFloat(bidValue),
    },

    {
      label: `Clear Fixed Bid`,
      action: () => updateTargetBids(true, "set_bid"),
      confirmationMessage: (
        <p className="fs-4 py-2">
          Are you sure you want to remove fixed bids for the selected {targetType}
          {selectedTargets?.length > 1 ? "s" : ""}?
        </p>
      ),
    },
    {
      label: `Set Temporary ${snakeCaseToSentenceCase(targetType)} Bid${selectedTargets?.length > 1 ? "s" : ""}`,
      action: () => scheduleBidBoost("fixed"),
      confirmationMessage:
        (
          !parseFloat(bidBoostFormData?.actionValue) ||
          !bidBoostFormData?.actionUnit ||
          !bidBoostFormData?.startDate ||
          !bidBoostFormData?.endDate
        ) ?
          <p className="fs-4 py-2">Please fill out all required fields.</p>
        : <p className="fs-4 py-2">
            Are you sure you want to set the fixed bid to {formatCurrency(bidBoostFormData?.actionValue, marketPlace)}{" "}
            for the selected {snakeCaseToSentenceCase(targetType).toLowerCase()}
            {selectedTargets?.length > 1 ? "s" : ""} from {moment(bidBoostFormData?.startDate).format("MMMM D")} to{" "}
            {moment(bidBoostFormData?.endDate).format("MMMM D")} (
            {(moment(bidBoostFormData?.endDate).diff(bidBoostFormData?.startDate, "days") + 1) * 24} hours)?
          </p>,
      actionFormFields: (
        <BulkAdPlanActionForm
          formData={bidBoostFormData}
          setFormData={setBidBoostFormData}
          actionTarget={"fixed bid"}
          hideActionTypeOptions={true}
          hidePercentOption={true}
          renderDateFields={true}
          forceCurrencyOption={true}
          renderKeepBidBoostField={true}
        />
      ),

      disableConfirmButton:
        !parseFloat(bidBoostFormData?.actionValue) ||
        !bidBoostFormData?.actionUnit ||
        !bidBoostFormData?.startDate ||
        !bidBoostFormData?.endDate,
    },
    {
      label: `Schedule ${snakeCaseToSentenceCase(targetType)} Bid Boost${selectedTargets?.length > 1 ? "s" : ""}`,
      action: () => scheduleBidBoost("boost"),
      confirmationMessage:
        (
          !parseFloat(bidBoostFormData?.actionValue) ||
          !bidBoostFormData?.actionUnit ||
          !bidBoostFormData?.startDate ||
          !bidBoostFormData?.endDate
        ) ?
          <p className="fs-4 py-2">Please fill out all required fields.</p>
        : <p className="fs-4 py-2">
            Are you sure you want to boost bids by{" "}
            {bidBoostFormData?.actionUnit === "percent" ?
              `${bidBoostFormData?.actionValue}%`
            : formatCurrency(bidBoostFormData?.actionValue, marketPlace)}{" "}
            for the selected {snakeCaseToSentenceCase(targetType).toLowerCase()}
            {selectedTargets?.length > 1 ? "s" : ""} from {moment(bidBoostFormData?.startDate).format("MMMM D")} to{" "}
            {moment(bidBoostFormData?.endDate).format("MMMM D")} (
            {(moment(bidBoostFormData?.endDate).diff(bidBoostFormData?.startDate, "days") + 1) * 24} hours)?
          </p>,
      actionFormFields: (
        <BulkAdPlanActionForm
          formData={bidBoostFormData}
          setFormData={setBidBoostFormData}
          actionTarget={"bid boost"}
          hideActionTypeOptions={true}
          renderDateFields={true}
          renderKeepBidBoostField={true}
        />
      ),

      disableConfirmButton:
        !parseFloat(bidBoostFormData?.actionValue) ||
        !bidBoostFormData?.actionUnit ||
        !bidBoostFormData?.startDate ||
        !bidBoostFormData?.endDate,
    },
    {
      label: `Schedule ${snakeCaseToSentenceCase(targetType)} Bid Reduction${selectedTargets?.length > 1 ? "s" : ""}`,
      action: () => scheduleBidBoost("reduction"),
      confirmationMessage:
        (
          !parseFloat(bidBoostFormData?.actionValue) ||
          !bidBoostFormData?.actionUnit ||
          !bidBoostFormData?.startDate ||
          !bidBoostFormData?.endDate
        ) ?
          <p className="fs-4 py-2">Please fill out all required fields.</p>
        : <p className="fs-4 py-2">
            Are you sure you want to reduce bids by{" "}
            {bidBoostFormData?.actionUnit === "percent" ?
              `${bidBoostFormData?.actionValue}%`
            : formatCurrency(bidBoostFormData?.actionValue, marketPlace)}{" "}
            for the selected {snakeCaseToSentenceCase(targetType).toLowerCase()}
            {selectedTargets?.length > 1 ? "s" : ""} from {moment(bidBoostFormData?.startDate).format("MMMM D")} to{" "}
            {moment(bidBoostFormData?.endDate).format("MMMM D")} (
            {(moment(bidBoostFormData?.endDate).diff(bidBoostFormData?.startDate, "days") + 1) * 24} hours)?
          </p>,
      actionFormFields: (
        <BulkAdPlanActionForm
          formData={bidBoostFormData}
          setFormData={setBidBoostFormData}
          actionTarget={"bid reduction"}
          hideActionTypeOptions={true}
          renderDateFields={true}
          renderKeepBidBoostField={true}
        />
      ),

      disableConfirmButton:
        !parseFloat(bidBoostFormData?.actionValue) ||
        !bidBoostFormData?.actionUnit ||
        !bidBoostFormData?.startDate ||
        !bidBoostFormData?.endDate,
    },
    {
      label: `Cancel Scheduled Bid Updates`,
      action: () => cancelScheduledBidBoosts(),
      confirmationMessage: (
        <p className="fs-4 py-2">
          Are you sure you want to cancel scheduled bid boosts and reductions for the selected{" "}
          {snakeCaseToSentenceCase(targetType).toLowerCase()}
          {selectedTargets?.length > 1 ? "s" : ""}?
        </p>
      ),
    },
    {
      label: `Update Bid Range${selectedTargets?.length > 1 ? "s" : ""}`,
      action: () => updateTargetBids(true, "set_bid_range"),
      actionFormFields: (
        <Row className="pb-5">
          <form>
            <Row className="d-flex justify-content-center align-items-center">
              <p className="pb-4 fs-4">
                Please enter your desired bid range for the selected {snakeCaseToSentenceCase(targetType).toLowerCase()}
                {selectedTargets?.length > 1 ? "s" : ""} using the fields below.
              </p>
              <Col xs={12} md={6} lg={4} className="px-5">
                <FormGroupTile
                  type="formcontrol"
                  formControlType={"number"}
                  handleChildFormElement={(key, value) => {
                    setMinBid(value);
                  }}
                  label={"Min Bid"}
                  placeholder={"Enter a minimum bid"}
                  defaultValue={minBid}
                />
              </Col>
              <Col xs={12} md={6} lg={4} className="px-5">
                <FormGroupTile
                  type="formcontrol"
                  formControlType={"number"}
                  handleChildFormElement={(key, value) => {
                    setMaxBid(value);
                  }}
                  label={"Max Bid"}
                  placeholder={"Enter a maximum bid"}
                  defaultValue={maxBid}
                />
              </Col>
            </Row>
          </form>
        </Row>
      ),
      confirmationMessage: (
        <>
          {parseFloat(minBid) >= parseFloat(maxBid) ?
            <p className="py-2 text-danger fs-5">
              Invalid bid range. Please ensure the minimum bid is a lower value than the maximum bid.
            </p>
          : parseFloat(minBid) && parseFloat(maxBid) ?
            <p className="py-2 fs-4">
              Are you sure you want to update the bid ranges for the selected{" "}
              {snakeCaseToSentenceCase(targetType).toLowerCase()}
              {selectedTargets?.length > 1 ? "s" : ""} to between {formatCurrency(minBid, marketPlace)} and{" "}
              {formatCurrency(maxBid, marketPlace)}?
            </p>
          : null}
        </>
      ),
      disableConfirmButton: !parseFloat(minBid) || !parseFloat(maxBid),
    },
    // Clear bid range for selected targets
    {
      label: `Remove Bid Range${selectedTargets?.length > 1 ? "s" : ""}`,
      action: () => updateTargetBids(true, "clear_bid_range"),
      confirmationMessage: (
        <p className="py-2 fs-4">
          Are you sure you want to remove the bid ranges for the selected{" "}
          {snakeCaseToSentenceCase(targetType).toLowerCase()}
          {selectedTargets?.length > 1 ? "s" : ""}?
        </p>
      ),
    },
    {
      label: `Add ${toTitleCase(targetType)} Tags`,
      action: () => {
        updatePlanTags();
      },
      actionFormFields: (
        <BulkTagsForm
          filledTags={filledTags}
          setFilledTags={setFilledTags}
          selected={selectedTargets}
          suggestedTags={customTags}
          taggedItemDescription={targetType}
          actionType={"add"}
        />
      ),
      confirmationMessage: (
        <p>
          Are you sure you want to add {filledTags?.length > 1 ? "these tags" : "this tag"} from the selected media plan
          {selectedTargets?.length > 1 ? "s" : ""}?
        </p>
      ),
      disableConfirmButton: !filledTags.length,
      hidden: channel.currentChannel === "walmart",
    },
    {
      label: `Remove ${toTitleCase(targetType)} Tags`,
      action: () => {
        updatePlanTags("remove");
      },
      actionFormFields: (
        <BulkTagsForm
          filledTags={filledTags}
          setFilledTags={setFilledTags}
          selected={selectedTargets}
          suggestedTags={customTags}
          taggedItemDescription={targetType}
          actionType={"add"}
        />
      ),
      confirmationMessage: (
        <p>
          Are you sure you want to remove {filledTags?.length > 1 ? "these tags" : "this tag"} from the selected media
          plan
          {selectedTargets?.length > 1 ? "s" : ""}?
        </p>
      ),
      disableConfirmButton: !filledTags.length,
      hidden: channel.currentChannel === "walmart",
    },
  ];

  const trellisManagedOptions = [
    {
      label: `Pause ${targetType === "target" ? "Target" : "Keyword"}${selectedTargets?.length > 1 ? "s" : ""}`,
      action: () => updateTargetState(TARGET_STATES.PAUSED.value),
      confirmationMessage: (
        <p className="fs-4 py-2">
          Are you sure you want to pause the selected {targetType}
          {selectedTargets?.length > 1 ? "s" : ""}?
        </p>
      ),
    },
    {
      label: `Enable ${targetType === "target" ? "Target" : "Keyword"}${selectedTargets?.length > 1 ? "s" : ""}`,
      action: () => updateTargetState(TARGET_STATES.ENABLED.value),
      confirmationMessage: (
        <p>
          Are you sure you want to enable the selected {targetType}
          {selectedTargets?.length > 1 ? "s" : ""}?
        </p>
      ),
    },
    {
      label: `Archive ${targetType === "target" ? "Target" : "Keyword"}${selectedTargets?.length > 1 ? "s" : ""}`,
      action: () => updateTargetState(TARGET_STATES.ARCHIVED.value),
      confirmationMessage: (
        <p>
          Are you sure you want to archive the selected {targetType}
          {selectedTargets?.length > 1 ? "s" : ""}?
        </p>
      ),
    },
    {
      label: `Negate ${targetType === "target" ? "Target" : "Keyword"}${selectedTargets?.length > 1 ? "s" : ""}`,
      action: () => negateTargets(),
      confirmationMessage: (
        <>
          <p className="fs-4 py-2">
            Are you sure you want to negate the selected {targetType === "target" ? "target" : "keyword"}
            {selectedTargets?.length > 1 ? "s" : ""}?
          </p>
          {skippedTargetCount ?
            <p className="fs-4 py-2">
              {skippedTargetCount} imported target{skippedTargetCount > 1 ? "s" : ""} will be ignored that{" "}
              {skippedTargetCount === 1 ? "is" : "are"} ineligible for negation.
            </p>
          : null}
        </>
      ),
    },
  ];

  if (!isNTMAd) {
    bulkActionOptions = bulkActionOptions.concat(trellisManagedOptions);
  }

  return { bulkActionOptions: bulkActionOptions ?? [], resetActionFormFields };
};

export default useTargetBulkActions;
